import type { AdminUserSummary } from "../types";
import { kycStage, KYC_STAGES } from "./kycOps";

/**
 * The directory renders names exactly as the banking API and Firestore stored them,
 * which is however the customer typed them at signup — usually all lowercase. Casing
 * them here keeps the fix in one place instead of scattering `text-transform` across
 * the pages that show a person.
 */
export function formatPersonName(value: string | null | undefined): string | null {
  const trimmed = value?.replace(/\s+/gu, " ").trim();
  if (!trimmed) return null;
  // Hyphens, apostrophes and periods separate words inside a single name token, so
  // "mary-jane o'brien j.r." has to case each segment, not just the first.
  return trimmed.replace(/[^\s\-'’.]+/gu, castWord);
}

function castWord(word: string): string {
  const lowered = word.toLowerCase();
  // Mixed case in the source is deliberate — "McDonald", "DeAngelo", "van Rijn" all
  // lose meaning if they are re-cased, so only all-lower and all-upper words move.
  if (word !== lowered && word !== word.toUpperCase()) return word;
  return lowered.charAt(0).toUpperCase() + lowered.slice(1);
}

export type UserSortKey = "name" | "state" | "access" | "customerGuid" | "createdAt";
export interface UserSort {
  key: UserSortKey;
  direction: "asc" | "desc";
}

export const DEFAULT_USER_SORT: UserSort = { key: "createdAt", direction: "desc" };

/**
 * Tapping the active column flips it; tapping a new one adopts the direction that
 * column is normally read in — newest first for dates, A to Z for everything else.
 */
export function nextUserSort(current: UserSort, key: UserSortKey): UserSort {
  if (current.key === key) return { key, direction: current.direction === "asc" ? "desc" : "asc" };
  return { key, direction: key === "createdAt" ? "desc" : "asc" };
}

function sortValue(user: AdminUserSummary, key: UserSortKey): string | number | null {
  if (key === "createdAt") {
    const parsed = user.createdAt ? Date.parse(user.createdAt) : Number.NaN;
    return Number.isNaN(parsed) ? null : parsed;
  }
  // Sort verification by its stage, not the raw string: the stages carry an order an
  // operator cares about, where `storing` next to `unverified` is just alphabetical.
  if (key === "state") return KYC_STAGES.indexOf(kycStage(user.state));
  if (key === "access") return user.banned ? 0 : 1;
  if (key === "customerGuid") return user.customerGuid?.trim() || null;
  return formatPersonName(user.name);
}

export function sortUsers(users: readonly AdminUserSummary[], sort: UserSort): AdminUserSummary[] {
  const direction = sort.direction === "asc" ? 1 : -1;
  const keyed = users.map((user, index) => ({ user, index, value: sortValue(user, sort.key) }));
  keyed.sort((left, right) => {
    // Rows with nothing in the sorted column sink to the bottom whichever way the
    // column points, so flipping direction never buries the populated rows.
    if (left.value === null || right.value === null) {
      if (left.value === right.value) return left.index - right.index;
      return left.value === null ? 1 : -1;
    }
    const compared = typeof left.value === "number" && typeof right.value === "number"
      ? left.value - right.value
      : String(left.value).localeCompare(String(right.value), undefined, { sensitivity: "base" });
    return compared === 0 ? left.index - right.index : compared * direction;
  });
  return keyed.map((entry) => entry.user);
}
